import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { CloneDetail, ClonePatch } from '@api/api';
import { endpoints, qk } from '../../api/endpoints';
import { SectionCard } from '../../components/common';
import { LoadingButton } from '../../components/LoadingButton';
import { useNotify } from '../../components/Notify';
import { domainError } from '../../lib/slug';
import { useIsOwner } from '../../auth/AuthProvider';

export const FEE_CURRENCIES = ['EUR', 'USD', 'GBP', 'UAH', 'PLN'];

type Form = {
  name: string;
  clientName: string;
  clientContact: string;
  licence: '' | 'rent' | 'sale';
  monthlyFee: string;
  feeCurrency: string;
  renewalDate: string;
  notes: string;
  domain: string;
  releaseId: number | '';
};

const toForm = (c: CloneDetail): Form => ({
  name: c.name,
  clientName: c.clientName,
  clientContact: c.clientContact ?? '',
  licence: c.licence ?? '',
  monthlyFee: c.monthlyFee === null ? '' : String(c.monthlyFee),
  feeCurrency: c.feeCurrency ?? 'EUR',
  renewalDate: c.renewalDate?.slice(0, 10) ?? '',
  notes: c.notes ?? '',
  domain: c.domain,
  releaseId: c.releaseId ?? '',
});

export function CloneSettingsTab({ clone: c }: { clone: CloneDetail }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const owner = useIsOwner();
  const [f, setF] = useState<Form>(() => toForm(c));
  useEffect(() => setF(toForm(c)), [c]);
  const draft = c.status === 'draft';
  const releases = useQuery({ queryKey: qk.releases, queryFn: endpoints.releases, enabled: draft });
  const dErr = draft ? domainError(f.domain) : null;
  const set = (p: Partial<Form>) => setF((x) => ({ ...x, ...p }));

  const save = useMutation({
    mutationFn: () => {
      const body: ClonePatch = {
        name: f.name.trim(),
        clientName: f.clientName.trim(),
        clientContact: f.clientContact.trim() || undefined,
        licence: f.licence || undefined,
        monthlyFee: f.monthlyFee === '' ? undefined : Number(f.monthlyFee),
        feeCurrency: f.feeCurrency || undefined,
        renewalDate: f.renewalDate || undefined,
        notes: f.notes,
      };
      if (draft) {
        body.domain = f.domain.trim();
        if (f.releaseId) body.releaseId = f.releaseId;
      }
      return endpoints.patchClone(c.id, body);
    },
    onSuccess: (d) => {
      qc.setQueryData(qk.clone(c.id), d);
      void qc.invalidateQueries({ queryKey: ['clones'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      notify.ok('Saved');
    },
    onError: (e) => notify.error(e),
  });

  const invalid = !f.name.trim() || !f.clientName.trim() || !!dErr || (f.monthlyFee !== '' && Number.isNaN(Number(f.monthlyFee)));

  return (
    <Box component="form" onSubmit={(e) => (e.preventDefault(), save.mutate())} sx={{ maxWidth: 900 }}>
      <Stack spacing={2}>
        <SectionCard title="Client">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Clone name" value={f.name} onChange={(e) => set({ name: e.target.value })} required disabled={!owner} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Client" value={f.clientName} onChange={(e) => set({ clientName: e.target.value })} required disabled={!owner} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Contact" value={f.clientContact} onChange={(e) => set({ clientContact: e.target.value })} placeholder="Name, phone, e-mail" disabled={!owner} />
            </Grid>
          </Grid>
        </SectionCard>
        <SectionCard title="Licence">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField select fullWidth label="Licence" value={f.licence} onChange={(e) => set({ licence: e.target.value as Form['licence'] })} disabled={!owner}>
                <MenuItem value="">—</MenuItem>
                <MenuItem value="rent">Rent (monthly)</MenuItem>
                <MenuItem value="sale">Sale (support fee)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={7} sm={3}>
              <TextField fullWidth label="Monthly fee" value={f.monthlyFee} onChange={(e) => set({ monthlyFee: e.target.value.replace(',', '.') })} inputProps={{ inputMode: 'decimal' }} error={f.monthlyFee !== '' && Number.isNaN(Number(f.monthlyFee))} disabled={!owner} />
            </Grid>
            <Grid item xs={5} sm={2}>
              <TextField select fullWidth label="Currency" value={f.feeCurrency} onChange={(e) => set({ feeCurrency: e.target.value })} disabled={!owner}>
                {FEE_CURRENCIES.map((x) => (
                  <MenuItem key={x} value={x}>
                    {x}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="date" label="Renewal date" value={f.renewalDate} onChange={(e) => set({ renewalDate: e.target.value })} InputLabelProps={{ shrink: true }} disabled={!owner} />
            </Grid>
          </Grid>
        </SectionCard>
        <SectionCard title="Domain and release">
          {!draft && (
            <Alert severity="info" sx={{ mb: 2 }}>
              The domain is fixed after provisioning. To change the release use <strong>Deploy</strong> on the overview tab.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7}>
              <TextField fullWidth label="Domain" value={f.domain} onChange={(e) => set({ domain: e.target.value.trim().toLowerCase() })} disabled={!draft || !owner} error={!!dErr} helperText={dErr ?? (draft ? 'A custom domain must point at the server IP (A record).' : ' ')} />
            </Grid>
            <Grid item xs={12} sm={5}>
              {draft ? (
                <TextField select fullWidth label="Release" value={f.releaseId} onChange={(e) => set({ releaseId: Number(e.target.value) })} disabled={!owner}>
                  {(releases.data ?? []).map((r) => (
                    <MenuItem key={r.id} value={r.id} disabled={r.status !== 'ready'}>
                      {r.name} {r.status !== 'ready' && `(${r.status})`}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField fullWidth label="Release" value={c.release ?? '—'} disabled />
              )}
            </Grid>
          </Grid>
        </SectionCard>
        <SectionCard title="Notes">
          <TextField fullWidth multiline minRows={3} value={f.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Anything worth remembering about this client" disabled={!owner} inputProps={{ 'aria-label': 'Notes' }} />
        </SectionCard>
        {owner && (
          <Stack direction="row" spacing={2} alignItems="center">
            <LoadingButton type="submit" variant="contained" loading={save.isPending} disabled={invalid}>
              Save
            </LoadingButton>
            <Typography variant="caption" color="text.secondary">
              Changes here don't touch the server.
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
